import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'sonner';
import ContactSection from './ContactSection';
import EventDetailsSection from './EventDetailsSection';
import GeneralEnquirySection from './GeneralEnquirySection';
import DateSelectionSection from './DateSelectionSection';
import AdditionalDetailsSection from './AdditionalDetailsSection';
import SubmitBar from './SubmitBar';
import LoadingOverlay from './LoadingOverlay';
import { submitEnquiry } from '@generated/server/enquiries';

interface EnquiryFormProps {
  onSuccess: (data: any) => void;
}

export default function EnquiryForm({ onSuccess }: EnquiryFormProps) {
  const [enquiryType, setEnquiryType] = useState<string>('');
  const [currentSection, setCurrentSection] = useState(1);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Initialize form with default values
  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      // Initial screen
      enquiryType: '',
      
      // Contact Information (shared)
      fullName: '',
      name: '',
      email: '',
      contactNumber: '',
      suburb: null as { address: string; lat: number; lng: number; city?: string; country?: string } | null,
      state: '',
      
      // Event-specific fields
      eventName: '',
      specificEnquiryType: '',
      pleaseSpecify: '',
      weddingType: '',
      venuePreferences: [] as string[],
      venueIfOther: '',
      subVenuePreferences: [] as string[],
      socialEventType: '',
      corporateEventType: '',
      businessName: '',
      industry: '',
      companyWebsite: '',
      externalCateringEventType: '',
      externalVenue: '',
      
      // Date selection (revised)
      eventDateType: '', // 'one-day' or 'multi-day'
      eventDate: null as string | null, // For one-day events
      eventDateRange: { 
        start: null as string | null, 
        end: null as string | null 
      }, // For multi-day events
      isFlexibleWithDates: 'no', // 'yes' or 'no'
      flexibleDates: [] as string[], // Multiple alternative dates for one-day events
      flexibleDateRange: {
        start: null as string | null,
        end: null as string | null
      }, // Alternative date range for multi-day events
      isRecurringEvent: 'no', // 'yes' or 'no'
      recurringFrequency: '', // How often event recurs
      
      // Additional details
      numberOfGuests: '',
      eventStyle: '',
      budget: '',
      budgetOther: '',
      tellUsMore: '',
      howDidYouHear: '',
      socialMediaPlatform: '',
      
      // General enquiry fields
      generalEnquiryType: '',
      generalEnquirySpecify: '',
      subject: '',
      hasSpecificDate: false,
      specificDate: null as string | null,
      
      // Privacy
      agreeToPrivacy: false,
    }
  });

  const handleEnquiryTypeSelect = (type: string) => {
    setEnquiryType(type);
    setCurrentSection(2);
    form.setValue('enquiryType', type as any);
  };

  const handleBackToSelector = () => {
    setEnquiryType('');
    setCurrentSection(1);
    form.reset();
  };

  const handleSubmit = async (data: any) => {
    try {
      setLoading(true);
      setProgress(0);
      
      // Simulate progress stages
      setProgress(10);
      
      // Add enquiryType to data
      const submissionData = {
        ...data,
        enquiryType: enquiryType as 'Event Enquiry' | 'General Enquiry'
      };
      
      setProgress(30);
      
      const result = await submitEnquiry({ data: submissionData });
      
      setProgress(90);
      
      if (result?.success) {
        setProgress(100);
        // Brief delay to show 100% before transition
        await new Promise(resolve => setTimeout(resolve, 400));
        
        onSuccess({
          itemId: result.itemId,
          itemName: result.itemName,
          email: data.email,
          enquiryType: enquiryType,
          specificEnquiryType: data.specificEnquiryType || data.generalEnquiryType,
          eventName: data.eventName,
          dateCount: result.dateCount
        });
      }
    } catch (error: any) {
      console.error('Enquiry submission error:', error);
      toast.error('Submission failed', {
        description: 'Please try again in a few minutes or alternatively contact us on +61 3 8623 9600',
        duration: 8000
      });
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  // Runs when validation fails: find the first invalid field on screen, scroll to it and focus it.
  const onFormInvalid = (errors: Record<string, any>) => {
    const keys = Object.keys(errors);
    if (keys.length === 0) return;

    const findTarget = (key: string): HTMLElement | null =>
      document.querySelector<HTMLElement>(`[data-field="${key}"]`) ??
      document.querySelector<HTMLElement>(`[name="${key}"]`) ??
      document.getElementById(key);

    // Pick whichever invalid field appears highest on the page
    const targets = keys
      .map((key) => ({ key, el: findTarget(key) }))
      .filter((t): t is { key: string; el: HTMLElement } => t.el !== null)
      .sort((a, b) => a.el.getBoundingClientRect().top - b.el.getBoundingClientRect().top);

    const first = targets[0];
    toast.error('Please check the form', {
      description: (first && errors[first.key]?.message) || 'Some fields need your attention'
    });
    if (!first) return;

    first.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = first.el.matches('input, textarea, button, [role="combobox"]')
      ? first.el
      : first.el.querySelector<HTMLElement>('input, textarea, button, [role="combobox"]');
    window.setTimeout(() => focusable?.focus({ preventScroll: true }), 350);
  };

  const onFormSubmit = async (data: any) => {
    // Run full validation
    const isValid = await form.trigger();
    
    if (!isValid) {
      const errors = form.formState.errors;
      const firstErrorKey = Object.keys(errors)[0];
      const firstError = errors[firstErrorKey as keyof typeof errors];
      
      if (firstError) {
        toast.error('Please check the form', {
          description: firstError.message || 'Some fields need your attention'
        });
        
        // Scroll to first error
        const element = document.querySelector(`[name="${firstErrorKey}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (element as HTMLElement).focus();
        }
      }
      
      return;
    }
    
    await handleSubmit(data);
  };

  return (
    <FormProvider {...form}>
      <div className="min-h-screen bg-background">
        {currentSection > 1 && (
          <div className="max-w-[1024px] mx-auto px-6 pt-6">
            <button
              type="button"
              onClick={handleBackToSelector}
              className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
            >
              ← Back to enquiry type selection
            </button>
          </div>
        )}
        
        <form id="enquiry-form" onSubmit={form.handleSubmit(onFormSubmit, onFormInvalid)} className="pb-32">
          {/* Section 1: Enquiry Type Selector */}
          {currentSection === 1 && (
            <div className="max-w-[1024px] mx-auto px-6 py-12 md:py-20">
              <div className="text-center mb-8 md:mb-12">
                <h1 className="text-[32px] md:text-[48px] font-bold text-foreground mb-3 leading-tight tracking-[0.02em]">
                  How can we help you today?
                </h1>
                <p className="text-sm text-muted-foreground">
                  Choose the type of enquiry to get started
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-5">
                <button
                  type="button"
                  onClick={() => handleEnquiryTypeSelect('Event Enquiry')}
                  className="p-6 md:p-8 border-2 border-border hover:border-foreground bg-background hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)] rounded-[4px] text-left transition-all"
                >
                  <h3 className="text-lg md:text-xl font-bold text-foreground mb-2 uppercase tracking-[0.025em]">
                    Event Enquiry
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Planning a wedding, corporate event, or special occasion
                  </p>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleEnquiryTypeSelect('General Enquiry')}
                  className="p-6 md:p-8 border-2 border-border hover:border-foreground bg-background hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)] rounded-[4px] text-left transition-all"
                >
                  <h3 className="text-lg md:text-xl font-bold text-foreground mb-2 uppercase tracking-[0.025em]">
                    Media & Employment
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Employment, media and other questions not related to an event
                  </p>
                </button>
              </div>
            </div>
          )}
          
          {/* Section 2+: Form Sections */}
          {currentSection >= 2 && enquiryType && (
            <div className="max-w-[1024px] mx-auto px-6 py-8 md:py-12">
              <ContactSection enquiryType={enquiryType} />
              
              {enquiryType === 'Event Enquiry' && (
                <>
                  <EventDetailsSection />
                  <DateSelectionSection />
                </>
              )}
              
              {enquiryType === 'General Enquiry' && (
                <GeneralEnquirySection />
              )}
              
              <AdditionalDetailsSection enquiryType={enquiryType} />
            </div>
          )}
        </form>
        
        {currentSection >= 2 && enquiryType && (
          <SubmitBar loading={loading} />
        )}
        
        {loading && <LoadingOverlay progress={progress} />}
      </div>
    </FormProvider>
  );
}
